// @ts-strict-ignore
import type { AviaryColorProps } from "aviary-tokens";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Button, QuantitySelector, Arrange, useToast, Tooltip } from "@aviary";
import type { ButtonAviarySize } from "@aviary/components/Button/Button.types";
import { AddedToCartButton } from "@emerson/components/AddedToCartButton/AddedToCartButton";
import { useCheckSimilarProductsAvailableQuery } from "@emerson/data/queries/CheckSimilarProductsAvailable/CheckSimilarProductsAvailable.query";
import { useAddToCartWholesale } from "@emerson/hooks/useAddToCartWholesale/useAddToCartWholesale";
import { l } from "@emerson/locales/i18n";
import { useWholesaleCartQuery } from "@shared/data/queries/WholesaleCartQuery/WholesaleCart.query";
import { e2eAttributes } from "@shared/e2eAttributes";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import type { EmersonEntryPoint } from "@shared/hooks/useAnalyticsLogger/EmersonAnalyticsEntryPoint";
import type { BrandAgreementEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import type { AnalyticsPayload } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { MultiCartType } from "@shared/types/MultiCartType";
import { StorePlatformType } from "@shared/types/graphqlGenerated";
import { Size } from "@shared/types/sizes";

import * as styles from "./AddToCartButton.styles";

type Props = {
  variantId: string;
  productId?: string;
  availabilityStatus: string;
  containsHemp?: boolean;
  analyticsEntryPoint: AnalyticsPayload["entry_point"];
  initialQuantity?: number;
  showQuantitySelector?: boolean;
  isMarginless?: boolean;
  isPrimary?: boolean;
  isFullWidth?: boolean;
  to?: string;
  similarProductsEnabled?: boolean;
  isAnyVariantUnavailable?: boolean;
  overrideButtonWrapper?: boolean;
  logProductViewEvent?: () => void;
  entryPoint: EmersonEntryPoint;
  brandAgreementEntryPoint: BrandAgreementEntryPoint;
  searchRank?: number;
  sku: string;
  brandName: string;
  defaultVariant: string;
  isCatalog?: boolean;
  buttonSize?: ButtonAviarySize;
  handleSimilarProductsClick?: () => void;
  isAddedButtonTextOnly?: boolean;
  withQuantitySelectorMargin?: boolean;
  isWrapped?: boolean;
  isQuickOrder?: boolean;
  resetQuantity?: boolean;
  onAddedToCart?: () => void;
  onClickAddToCart?: () => void;
  isDisabled?: boolean;
} & AviaryColorProps;

const AddToCartButton = ({
  similarProductsEnabled = false,
  isAnyVariantUnavailable,
  variantId,
  productId,
  containsHemp,
  availabilityStatus,
  analyticsEntryPoint,
  initialQuantity = 1,
  showQuantitySelector = true,
  overrideButtonWrapper = false,
  isDisabled = false,
  isColor = "info",
  isMarginless = false,
  isPrimary = false,
  isFullWidth = false,
  buttonSize = Size.MEDIUM,
  logProductViewEvent,
  entryPoint,
  brandAgreementEntryPoint,
  searchRank,
  brandName,
  sku,
  defaultVariant,
  isCatalog,
  handleSimilarProductsClick,
  isAddedButtonTextOnly = true,
  withQuantitySelectorMargin = true,
  isWrapped = true,
  isQuickOrder,
  onAddedToCart,
  resetQuantity,
  onClickAddToCart,
}: Props) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const isDesktop = tablet.greaterThan;
  const { makeToast } = useToast();
  const logAnalyticsEvent = useAnalyticsLogger();

  const [quantity, setQuantity] = useState(initialQuantity);

  const { data } = useWholesaleCartQuery();

  useEffect(() => {
    if (initialQuantity > quantity || resetQuantity) {
      setQuantity(initialQuantity);
    }
  }, [initialQuantity, resetQuantity]);

  const isSearchRankProvided: boolean = typeof searchRank === "number";
  const multicartType = data?.currentOffice?.wholesaleCart?.isCreatedByPractitioner
    ? MultiCartType.CUSTOMER
    : MultiCartType.ADMIN;

  const onCompleted = () => {
    onAddedToCart?.();
    makeToast("success", t(l.sharedCommon.AddedToCart));
  };

  const logAddToCartEvent = () => {
    logAnalyticsEvent({
      eventName: AnalyticsEvent.EMERSON_CHECKOUT_ADD_TO_CART,
      payload: {
        entry_point: entryPoint,
        default_variant: defaultVariant,
        ...(isSearchRankProvided && { search_rank: searchRank }),
        product_status: availabilityStatus.toLowerCase(),
        brand: brandName,
        sku,
        multicart_type: multicartType,
        contains_hemp: containsHemp,
      },
    });
  };

  const addToCart = () => {
    logAddToCartEvent();
    onClickAddToCart?.();
    wholesaleAddToCart({
      variables: {
        input: {
          cart: [
            {
              variantId,
              quantity,
            },
          ],
          storePlatform: StorePlatformType.Emerson,
        },
      },
    });
  };

  const { wholesaleAddToCart, loading, renderMapAgreementModal } = useAddToCartWholesale(
    addToCart,
    brandAgreementEntryPoint,
    onCompleted
  );

  const updateQuantity = newQuantity => {
    setQuantity(newQuantity);
  };

  const { data: availabilityData } = useCheckSimilarProductsAvailableQuery({
    variables: { productId },
    skip: !isAnyVariantUnavailable,
  });

  const isNotAvailable =
    availabilityStatus?.toLowerCase() === "unavailable" ||
    availabilityStatus?.toLowerCase() === "discontinued" ||
    availabilityStatus?.toLowerCase() === "restricted" ||
    availabilityStatus?.toLowerCase() === "n/a" ||
    isDisabled;

  const isRestrictedForWholesale = availabilityStatus?.toLowerCase() === "restricted";

  const renderRestrictedForWholesaleText = () => t(l.sharedCommon.restrictedForWholesale);

  const showSimilarProductsButton =
    similarProductsEnabled &&
    availabilityData?.viewer?.product?.anySimilarProductAvailable &&
    isAnyVariantUnavailable;

  const addedLineItem = data?.currentOffice?.wholesaleCart?.lineItems.find(
    item => item.variant.id === variantId
  );

  const renderButtonText = () => {
    return t(l.sharedCommon.OutOfStock);
  };

  const renderActionButtons = () => {
    if (isRestrictedForWholesale) {
      return (
        <Button size={buttonSize} disabled isColor="danger" isFullWidth>
          {renderRestrictedForWholesaleText()}
        </Button>
      );
    }

    if (isNotAvailable && !similarProductsEnabled && !isQuickOrder) {
      return (
        <Tooltip tooltipContent={t(l.sharedCommon.ProductOutOfStockOrDiscontinued)} placement="top">
          <Button disabled isColor="danger">
            {renderButtonText()}
          </Button>
        </Tooltip>
      );
    }

    if (showSimilarProductsButton) {
      return (
        <Button
          size={buttonSize}
          isColor={isPrimary ? "primary" : "system"}
          isFullWidth
          onClick={handleSimilarProductsClick ?? logProductViewEvent}
        >
          {t(l.catalog.ViewSimilarProducts)}
        </Button>
      );
    }

    if (addedLineItem) {
      return (
        <AddedToCartButton
          variantId={variantId}
          analyticsEntryPoint={analyticsEntryPoint}
          showQuantityIndicator={showQuantitySelector}
          isColor={isColor}
          hasMargin={!isFullWidth && withQuantitySelectorMargin}
          isFullWidth={!isAddedButtonTextOnly || isCatalog}
          isHorizontal={isCatalog || isQuickOrder}
          buttonSize={buttonSize}
          isText={isAddedButtonTextOnly}
        />
      );
    }

    return (
      <>
        {!!showQuantitySelector && (
          <span css={buttonSize === Size.LARGE && styles.quantitySelectorButton}>
            <QuantitySelector
              value={quantity}
              handleSetValue={updateQuantity}
              hasMargin={!isFullWidth && withQuantitySelectorMargin}
              isFullWidth={isFullWidth}
              disabled={isNotAvailable}
            />
          </span>
        )}
        <Button
          isColor={isPrimary ? "primary" : "system"}
          isLoading={loading}
          onClick={addToCart}
          disabled={isNotAvailable}
          data-e2e={e2eAttributes.addProductToCart}
          size={buttonSize}
        >
          {t(l.sharedCommon.AddToCart)}
        </Button>
        {renderMapAgreementModal()}
      </>
    );
  };

  if (isFullWidth) {
    return (
      <div
        css={[
          styles.fullWidth,
          !showSimilarProductsButton &&
            !isRestrictedForWholesale &&
            !addedLineItem &&
            styles.buttonsWrapper,
          overrideButtonWrapper && styles.overrideButtonWrapper,
          !showQuantitySelector && styles.singleActionButton,
          isRestrictedForWholesale && styles.singleActionButton,
        ]}
      >
        {renderActionButtons()}
      </div>
    );
  }

  return (
    <Arrange
      css={styles.container}
      justify={isDesktop ? "center" : "spaceBetween"}
      alignment="center"
      spacing={isMarginless ? "none" : "medium"}
      isWrapped={!!isWrapped && !!isDesktop && !isQuickOrder}
    >
      {renderActionButtons()}
    </Arrange>
  );
};

export { AddToCartButton };
